import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { protocolosKeys } from '@/features/protocolos/hooks/useProtocolos';

import { coprocultivoApi } from '../api';
import type { Coprocultivo, CoprocultivoCreate, CoprocultivoUpdate } from '../types';

const QUERY_KEY_BASE = ['analisis-coprocultivo'] as const;

export const coprocultivoKeys = {
  all: QUERY_KEY_BASE,
  byProtocolo: (uidProtocolo: string) =>
    [...QUERY_KEY_BASE, 'by-protocolo', uidProtocolo] as const,
  detail: (uidProtocolo: string, uidAnalisis: string) =>
    [...QUERY_KEY_BASE, uidProtocolo, uidAnalisis] as const,
};

export function useCoprocultivoListByProtocolo(uidProtocolo: string | undefined) {
  return useQuery({
    queryKey: uidProtocolo ? coprocultivoKeys.byProtocolo(uidProtocolo) : coprocultivoKeys.all,
    queryFn: ({ signal }) => coprocultivoApi.listByProtocolo(uidProtocolo!, signal),
    enabled: !!uidProtocolo,
  });
}

export function useCoprocultivo(
  uidProtocolo: string | undefined,
  uidAnalisis: string | undefined
) {
  return useQuery({
    queryKey:
      uidProtocolo && uidAnalisis
        ? coprocultivoKeys.detail(uidProtocolo, uidAnalisis)
        : coprocultivoKeys.all,
    queryFn: ({ signal }) => coprocultivoApi.getById(uidProtocolo!, uidAnalisis!, signal),
    enabled: !!(uidProtocolo && uidAnalisis),
  });
}

export function useCreateCoprocultivo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      data,
    }: {
      uidProtocolo: string;
      data: CoprocultivoCreate;
    }) => coprocultivoApi.create(uidProtocolo, data),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({
        queryKey: coprocultivoKeys.byProtocolo(uidProtocolo),
      });
    },
  });
}

export function useUpdateCoprocultivo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
      data,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
      data: CoprocultivoUpdate;
    }) => coprocultivoApi.update(uidProtocolo, uidAnalisis, data),
    onSuccess: (updated: Coprocultivo, { uidProtocolo, uidAnalisis }) => {
      void queryClient.invalidateQueries({
        queryKey: coprocultivoKeys.byProtocolo(uidProtocolo),
      });
      queryClient.setQueryData(coprocultivoKeys.detail(uidProtocolo, uidAnalisis), updated);
    },
  });
}

export function useDeleteCoprocultivo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      uidProtocolo,
      uidAnalisis,
    }: {
      uidProtocolo: string;
      uidAnalisis: string;
    }) => coprocultivoApi.remove(uidProtocolo, uidAnalisis),
    onSuccess: (_data, { uidProtocolo }) => {
      void queryClient.invalidateQueries({
        queryKey: coprocultivoKeys.byProtocolo(uidProtocolo),
      });
      void queryClient.invalidateQueries({ queryKey: protocolosKeys.detail(uidProtocolo) });
    },
  });
}
